import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';

import {
  Popup,
  I18n,
  TextInput,
  Heading,
  Icon,
  Button,
  Checkbox,
  Link,
  Preloader,
} from 'components';

import { formatLogin } from 'utils/formatString';

import styles from './styles.pcss';

import { NAME_MAX_LEN } from './constants';
import { getNicknameInputError, normalizeName, validateNickname } from './utils';

const cx = classNames.bind(styles);

const getBalance = (props) => ((props.postpaidLimit && props.postpaidLimit.balance) || '').toString();

const getDefaultState = (props) => ({
  nickName: props.nickName || '',
  isLoginDefault: props.ssoLoginDefault || false,
  isDirty: false,
  isLimited: !!getBalance(props),
  spendingLimit: getBalance(props),
  nickNameError: '',
});

class AccountEditPopup extends PureComponent {
  constructor(props) {
    super(props);

    this.state = getDefaultState(props);
    this.nickNameErrorSync = '';
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    this.setState(getDefaultState(nextProps));
    this.nickNameErrorSync = '';
  }

  handleNickNameChange = (nextValue) => {
    const inputError = getNicknameInputError(nextValue);

    this.nickNameErrorSync = inputError;

    if (inputError) {
      this.setState({
        nickNameError: inputError,
      });
      return;
    }

    this.setState({
      nickName: nextValue,
      isDirty: true,
      nickNameError: '',
    });
  };

  handleNickNameBlur = () => {
    if (this.nickNameErrorSync) {
      this.setState({
        nickNameError: this.nickNameErrorSync,
      });
      return;
    }

    const err = validateNickname(this.state.nickName);

    this.nickNameErrorSync = err;

    this.setState({
      nickNameError: err,
    });
  };

  saveAccount(normalizedNickName) {
    const {
      login, onSave, isPostpaid, banLogin, isMobile,
    } = this.props;

    const { spendingLimit, isLoginDefault, isLimited } = this.state;

    let postpaidLimit;

    if (isPostpaid) {
      postpaidLimit = isLimited ? parseInt(spendingLimit, 10) || 0 : 0;
    }

    onSave({
      isMobile,
      nickName: normalizedNickName,
      banLogin,
      postpaidLimit,
      linkedAccountLogin: login,
      isDefaultSsoLogin: isLoginDefault,
    });
  }

  render() {
    const {
      isOpened,
      onClose,
      isMobile,
      login,
      isPostpaid,
      isAssociatedAccountUpdating,
    } = this.props;

    const {
      nickName, spendingLimit, isDirty, isLimited, nickNameError,
    } = this.state;

    if (!login) {
      return null;
    }

    const normalizedNickName = nickName === '' ? '' : normalizeName(nickName);
    const submitError = validateNickname(nickName);
    const hasAnyError = !!nickNameError || !!this.nickNameErrorSync || !!submitError;

    const isSaveDisabled = !isDirty || hasAnyError || isAssociatedAccountUpdating;

    return (
      <Popup
        className={cx('popup')}
        opened={isOpened}
        onClose={() => !isAssociatedAccountUpdating && onClose()}
      >
        <form
          onSubmit={(e) => {
            e.preventDefault();

            if (this.nickNameErrorSync || nickNameError) {
              return;
            }

            const err = validateNickname(nickName);
            if (err) {
              this.nickNameErrorSync = err;
              this.setState({
                nickNameError: err,
              });
              return;
            }

            if (!isDirty || isAssociatedAccountUpdating) {
              return;
            }

            this.saveAccount(normalizedNickName);
          }}
        >
          <Heading level={3} className={cx('popupTitle')}>
            {isMobile ? (
              <Icon name="phone" className={cx('phoneIcon')} />
            ) : (
              <Icon name="home" className={cx('homeIcon')} />
            )}

            <span className={cx('phoneNumber')}>
              {formatLogin(login, !isMobile)}
            </span>
          </Heading>

          {isAssociatedAccountUpdating ? (
            <div className={cx('preloaderWrapper')}>
              <Preloader className={cx('preloader')} size="80" />
            </div>
          ) : (
            <div>
              <div className={cx('inputWrapper')}>
                <I18n
                  className={cx('inputLabel')}
                  path="settings.numberManagement.editPopup.nicknameLabel"
                />

                <TextInput
                  className={cx('nicknameInput')}
                  value={nickName}
                  maxLength={NAME_MAX_LEN}
                  status={nickNameError ? 'fail' : undefined}
                  keepErrorIndent
                  onBlur={() => this.handleNickNameBlur()}
                  onChange={(value) => this.handleNickNameChange(value)}
                />
              </div>

              {isPostpaid && (
                <div className={cx('inputWrapper')}>
                  <Checkbox
                    className={cx('inputLabel')}
                    name={`limit-${login}`}
                    checked={isLimited}
                    handleChange={() => this.setState({
                      isLimited: !isLimited,
                      isDirty: true,
                    })}
                  >
                    <I18n path="settings.numberManagement.editPopup.limitLabel" />
                  </Checkbox>

                  <TextInput
                    className={cx('spendingLimitInput')}
                    value={spendingLimit}
                    disabled={!isLimited}
                    filterInput={(value) => value.replace(/(^0+)|\D/g, '')}
                    onChange={(value) => (value !== spendingLimit
                      ? this.setState({
                        spendingLimit: value,
                        isDirty: true,
                      })
                      : null)}
                  />

                  <I18n
                    className={cx('currency')}
                    path="settings.numberManagement.editPopup.rubles"
                  />
                </div>
              )}

              <div className={cx('footer')}>
                <Button type="submit" className={cx('saveButton')} disabled={isSaveDisabled}>
                  <I18n path="common.saveChanges" />
                </Button>

                <Link onClick={() => onClose()}>
                  <I18n path="common.cancel" />
                </Link>
              </div>
            </div>
          )}
        </form>
      </Popup>
    );
  }
}

AccountEditPopup.propTypes = {
  isOpened: PropTypes.bool,
  isAssociatedAccountUpdating: PropTypes.bool,
  isPostpaid: PropTypes.bool,
  login: PropTypes.string,
  banLogin: PropTypes.string,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  isMobile: PropTypes.bool,
};

export default AccountEditPopup;
